
import { Options, Vue } from 'vue-class-component';
import { Prop, Ref, Watch } from 'vue-property-decorator';
import { CamerasService, ExternalVmsService, VmsService } from '@/api';
import { request as __request } from '@/api/core/request';
import { authModule } from '@/store/auth';
import { configModule } from '@/store/config';
import { dataModule } from '@/store/data';
import NIcon from '@/uikit/icons/NIcon.vue';
import NLoadingCircle from '@/uikit/loading/NLoadingCircle.vue';
import { Debounce } from '@/common/debounce-decorator';
import { isDefined } from '@/common/utils';
import { IObjectItem } from '@/components/video-player/objects/timeline-object';
import TimelineChunks from '@/components/video-player/objects/vms/chunks';
import VideoPlayerInfoPanel from '@/components/video-player/panels/VideoPlayerInfoPanel.vue';
import VideoPlayerPtzPanel from '@/components/video-player/panels/VideoPlayerPtzPanel.vue';
import VideoPlayerSettings from '@/components/video-player/panels/VideoPlayerSettings.vue';
import debounce from '@/components/video-player/utils/debounce';
import SimpleTimer from '@/components/video-player/utils/simple-timer';
import VideoPlayerBottomBar from '@/components/video-player/VideoPlayerBottomBar.vue';
import VideoPlayerControls from '@/components/video-player/VideoPlayerControls.vue';
import VideoPlayerExport from '@/components/video-player/VideoPlayerExport.vue';
import VideoPlayerLineLayer from '@/components/video-player/VideoPlayerLineLayer.vue';
import VideoPlayerOverlay from '@/components/video-player/VideoPlayerOverlay.vue';
import VideoPlayerRender from '@/components/video-player/VideoPlayerRender.vue';
import VideoPlayerState from '@/components/video-player/VideoPlayerState';
import VideoPlayerThumbnail from '@/components/video-player/VideoPlayerThumbnail.vue';
import VideoPlayerTimeline from '@/components/video-player/VideoPlayerTimeline.vue';

enum TimeType {
  Live = 'live',
  Archive = 'archive',
  ExternalArchive = 'external-archive',
  NotFound = 'not_found'
}

interface IStreamProps {
  width: number;
  height: number;
  fps: number;
}

@Options({
  name: 'VideoPlayer',
  components: {
    NIcon,
    NLoadingCircle,
    VideoPlayerBottomBar,
    VideoPlayerControls,
    VideoPlayerExport,
    VideoPlayerInfoPanel,
    VideoPlayerLineLayer,
    VideoPlayerOverlay,
    VideoPlayerPtzPanel,
    VideoPlayerRender,
    VideoPlayerSettings,
    VideoPlayerThumbnail,
    VideoPlayerTimeline
  }
})
export default class VideoPlayer extends Vue {
  @Prop({ type: String, default: 'cameras' })
  readonly model!: string;

  @Prop({ type: Number, required: true })
  readonly cameraId!: number;

  @Prop({ type: Number, required: true })
  readonly timeFrom!: number;

  @Prop({ type: Boolean, default: false })
  readonly reconnectOnClose!: boolean;

  @Prop({ type: Boolean, default: true })
  readonly showTimeline!: boolean;

  @Prop({ type: Boolean, default: true })
  readonly showControls!: boolean;

  @Prop({ type: Boolean, default: true })
  readonly showRewinds!: boolean;

  @Prop({ type: Boolean, default: true })
  readonly showBottomBar!: boolean;

  @Prop({ type: Boolean, default: false })
  readonly closeable!: boolean;

  @Prop({ type: Boolean, default: true })
  readonly showOverlay!: boolean;

  @Prop({
    type: Array,
    default() {
      return ['faces', 'bodies', 'cars'];
    }
  })
  readonly showBboxObjects!: string[];

  playerRender: VideoPlayerRender | null = null;

  @Ref('playerTimeline')
  playerTimeline!: VideoPlayerTimeline;

  private lastTimelineTime = 0;

  isLive = false;
  hasPtzPanel = false;
  hasInfoPanel = false;
  hasSettingsPanel = false;

  settings = new VideoPlayerState();

  eventType = 'events';
  frameObjects = {};
  streamProps: IStreamProps = { width: 640, height: 480, fps: 30 };
  container = { top: 0, left: 0, width: 640, height: 480, scale: 1 };
  playerContent!: HTMLDivElement;

  exportTimeFrom = 0;
  exportTimeTo = 0;

  wsUrl = '';
  playerState = '';
  exportMode = false;
  thumbSrc = '';
  thumbCss = {};
  currentTime = 0;
  oldState = TimeType.NotFound;
  newState = TimeType.NotFound;
  positionStartTime = 0;
  restartRenderDebounced: any = false;

  isFirstPlay = true; // если это первое открытие из эпизода

  // для скрытия контролов
  hoverFlag = true;
  hoverTimer = new SimpleTimer();
  mousemove() {
    this.hoverFlag = true;
    this.hoverTimer.setTimeout(() => {
      this.hoverFlag = false;
    }, 10000);
  }

  readonly observer = new ResizeObserver(([entry]) => isDefined(entry) && this.resizeContainer(entry.contentRect));

  // Если заменить @Ref(), возникают проблемы FFSEC-4583
  setPlayerRenderRef(el: any) {
    if (el) {
      this.playerRender = el;
    } else {
      this.playerRender = null;
    }
  }

  /* обработка settings.brightness, settings.contrast, settings.saturation */
  get cssFilters() {
    return { filter: `brightness(${this.settings.brightness / 50}) contrast(${this.settings.contrast / 50}) saturate(${this.settings.saturation / 50})` };
  }

  get restartIntervalSec() {
    return this.isLive ? 600 : 0;
  }

  get syncLiveIntervalSec() {
    return this.isLive ? 6 : 0;
  }

  zoomHandler(delta: number) {
    this.$refs.playerTimeline.zoomHandler(delta);
  }

  /* обработка settings.resolution и settings.videoQuality */
  get resolution() {
    const [width, height] = this.settings.resolution.split('x');
    return { width: Number(width), height: Number(height) };
  }
  restartRender() {
    if (this.oldState === TimeType.Archive) {
      this.oldState = TimeType.NotFound;
      if (this.playerRender) {
        this.timelinePositionChange(this.playerRender.realTimestamp);
      } else {
        // console.log('PlayerRender is null!');
      }
    }
  }
  restartRenderWithDebounce() {
    if (!this.restartRenderDebounced) {
      this.restartRenderDebounced = debounce(this.restartRender, 1000);
    }
    this.restartRenderDebounced();
  }

  @Watch('settings.resolution')
  resolutionWatcher() {
    this.restartRenderWithDebounce();
  }

  @Watch('settings.videoQuality')
  videoQualityWatcher() {
    this.restartRenderWithDebounce();
  }

  playerContentRef(el: HTMLDivElement) {
    this.playerContent = el;
  }

  get frameObjectsEx() {
    if (this.oldState === TimeType.Live) {
      return this.frameObjects;
    }
    return [];
  }

  get isPause() {
    return this.playerState === 'paused';
  }

  getLiveUrl(cameraId: number, model: string) {
    let secure = window.location.protocol === 'https:';
    let configServerUrl = configModule.config?.server?.url;
    let serverUrl = configServerUrl && configServerUrl !== '/' ? configServerUrl : window.location.host + '/';
    let protocol = secure ? 'wss://' : 'ws://';
    let wsUrl = protocol + serverUrl.replace(/^(https?:)?\/\//i, '') + model + '/' + cameraId + '/stream/?token=' + authModule.token;
    return wsUrl;
  }

  async getArchiveUrl(cameraId: number, timeFrom: number) {
    try {
      const archive = await VmsService.vmsArchiveRetrieve(
        new Date(timeFrom * 1000).toISOString(),
        cameraId,
        this.resolution.height,
        this.resolution.width,
        this.settings.videoQuality
      );
      return archive.url;
    } catch (e) {
      console.error('video-player: playArchive error', e);
    }
    return '';
  }

  setTimelinePosition(time: number) {
    if (this.playerTimeline) {
      this.playerTimeline.setPosition(time);
      this.playerTimeline.autoOffset(time);
    }
    this.currentTime = time;
  }

  videoPositionChange(time: number) {
    this.setTimelinePosition(time);
  }

  play() {
    if (this.playerRender) {
      this.playerRender.play();
    } else {
      // console.log('PlayerRender is null!');
    }
  }

  pause() {
    if (this.playerRender) {
      this.isLive = false;
      this.playerRender.pause();
    }
  }

  // Based on https://ntechlab.atlassian.net/browse/FFSEC-2305
  // https://gitlab.int.ntl/ntech/universe/-/blob/ecf501a5334c5882cfb7f299db4008edf7de39f7/ffsecurity-ui/src/components/video-player/video-player.vue
  async playFromExternalVms() {
    try {
      // TODO: refactor this in future
      // use this.playerRender.realTimestamp
      // or just delete it
      // console.log('this.lastTimelineTime', this.lastTimelineTime);
      if (!this.lastTimelineTime) {
        this.lastTimelineTime = this.timeFrom;
        // console.log('evms inited!');
      }

      // Получаем ID интеграции
      const cameraObject = this.settings.camera;
      if (!cameraObject) {
        console.error('video-player: top5vms cameraObject not found!');
        return false;
      }
      if (!cameraObject.external_vms) {
        console.warn('video-player: top5vms cameraObject.external_vms not found!');
        return false;
      }
      const external_vms_id = cameraObject.external_vms;
      const camera_id = cameraObject.id;
      const time_from = new Date(this.lastTimelineTime * 1000).toISOString();
      const time_to = new Date((this.lastTimelineTime + 15 * 60 * 60) * 1000).toISOString();

      const filter: any = {
        camera: camera_id,
        time_from,
        time_to,
        start_time: this.lastTimelineTime
      };

      const config = configModule.config.external_vms;
      if (config.max_width) {
        filter.max_width = config.max_width;
      }
      if (config.max_height) {
        filter.max_height = config.max_height;
      }
      if (config.quality) {
        filter.quality = config.quality;
      }

      const archive: any = await __request({
        method: 'GET',
        path: `/external-vms/${external_vms_id}/archive/`,
        query: filter
      });

      this.wsUrl = archive.url;
      let external_link = archive.external_link;

      this.settings.onWsOpened = () => {
        this.settings.onWsOpened = null;
        try {
          __request({
            method: 'POST',
            path: `/external-vms/${external_vms_id}/play/`,
            query: {
              camera: camera_id,
              time_from,
              time_to,
              url: external_link
            }
          });
        } catch (e) {
          console.error('video-player: top5vms play error', e);
        }
      };
    } catch (e) {
      console.error('video-player: top5vms archive error', e);
      return false;
    }
    return true;
  }

  // Update player state when time changes
  async timelinePositionChange(time: number) {
    this.lastTimelineTime = time;
    if (this.settings.isCameraRemoved) {
      console.warn('CameraRemoved');
      return;
    }

    // при необходимости будет заполнено в playFromExternalVms()
    this.settings.onWsOpened = null;

    // Get mode by time
    if (time < new Date().getTime() / 1000) {
      this.newState = TimeType.Archive;
    } else {
      this.newState = TimeType.Live;
    }

    // Connection was closed
    if (this.playerState === 'closed' || this.playerState === 'error') {
      this.oldState = TimeType.NotFound;
    }

    // Progress offline videos - live mode
    if (this.model === 'videos') {
      this.newState = TimeType.Live;
    }

    // Live mode
    if (this.newState === TimeType.Live) {
      this.settings.onWsOpened = null;
      this.wsUrl = this.getLiveUrl(this.cameraId, this.model);
      this.positionStartTime = new Date().getTime() / 1000;
      this.isLive = true;
    } else {
      this.positionStartTime = 0;
      this.isLive = false;
    }

    // Archive mode
    if (this.newState === TimeType.Archive) {
      // Check time in chunks
      await this.settings.timelineChunks.loadIfEmpty(this.cameraId);
      const timeInChunks = this.settings.isTimeInChunks(time);

      // Check external vms
      let isExternalVms = configModule.getBoolean('external_vms.enabled', false);
      if (!this.settings.camera?.external_vms) {
        isExternalVms = false;
      }

      // console.log({ timeInChunks });
      // Logic for deciding where and how to play archived video
      let runPlayType = '';
      if (timeInChunks) {
        runPlayType = 'archive';
      } else if (isExternalVms) {
        runPlayType = 'external_vms';
      } else {
        if (this.isFirstPlay) {
          runPlayType = 'error';
        } else {
          runPlayType = 'archive';
        }
      }

      // console.log('RPT resolved:', runPlayType);

      if (runPlayType === 'archive') {
        // play from internal vms
        if (this.oldState === TimeType.Archive) {
          if (this.playerRender) {
            this.playerRender.seek(time);
          } else {
            // console.log('Player render is null!');
          }
        } else {
          this.wsUrl = await this.getArchiveUrl(this.cameraId, time);
        }
      }

      if (runPlayType === 'external_vms') {
        // Play from external vms
        this.wsUrl = ''; // Will be filled i playFromExternalVms()
        const success = await this.playFromExternalVms();
        if (success) {
          this.newState = TimeType.ExternalArchive;
        } else {
          this.wsUrl = '';
          this.newState = TimeType.NotFound;
        }
      }

      if (runPlayType === 'error') {
        this.wsUrl = '';
        this.newState = TimeType.NotFound;
        this.playerState = 'error';
      }
    }

    this.settings.canChangeQuality = this.newState === TimeType.Archive;
    this.settings.canChangeObjects = this.newState === TimeType.Live;

    // need for debug
    // console.log(this.oldState, this.newState);
    this.oldState = this.newState;
    this.isFirstPlay = false;
  }

  async timelinePositionChangeForce(time: number) {
    await this.timelinePositionChange(time);
    this.playerTimeline && this.playerTimeline.autoOffsetForceNext();
  }

  async mounted() {
    this.attachPlayerContentResizeObserver();

    this.settings.model = this.model;
    this.settings.cameraId = this.cameraId;
    if (this.model !== 'videos') await this.settings.fillCameraInfoById();

    // Let's try to play the video
    this.timelinePositionChangeForce(this.timeFrom);

    if (this.showTimeline && this.playerTimeline) {
      this.setTimelinePosition(this.timeFrom); // Set timeline cursor
      this.playerTimeline.updateObjectsWithDebounce(); // Load timeline Objects
    }
  }

  beforeUnmount() {
    this.detachPlayerContentResizeObserver();
  }

  thumbChanged({ src, css }: { src: string; css: any }) {
    this.thumbSrc = src;
    this.thumbCss = css;
  }

  exportTimeChange({ timeFrom, timeTo }: { timeFrom: number; timeTo: number }) {
    this.exportTimeFrom = timeFrom;
    this.exportTimeTo = timeTo;
  }

  @Watch('camera')
  cameraWatcher() {
    this.timelinePositionChange(new Date().getTime() / 1000 + 10);
  }

  // TODO: replace to @Watcher(videoState) in future
  videoSeeked() {
    this.playerTimeline && this.playerTimeline.autoOffsetForceNext();
  }

  get containerStyles() {
    return {
      top: this.container.top + 'px',
      left: this.container.left + 'px',
      width: this.container.width + 'px',
      height: this.container.height + 'px'
    };
  }

  resizeContainer(rect: DOMRect) {
    const scaleFactorH = rect.width / this.streamProps.width;
    const scaleFactorV = rect.height / this.streamProps.height;
    if (scaleFactorH * this.streamProps.height > rect.height) {
      this.container.top = 0;
      this.container.left = (this.streamProps.width * (scaleFactorH - scaleFactorV)) / 2;
      this.container.width = this.streamProps.width * scaleFactorV;
      this.container.height = this.streamProps.height * scaleFactorV;
      this.container.scale = scaleFactorV;
    } else {
      this.container.top = (this.streamProps.height * (scaleFactorV - scaleFactorH)) / 2;
      this.container.left = 0;
      this.container.width = this.streamProps.width * scaleFactorH;
      this.container.height = this.streamProps.height * scaleFactorH;
      this.container.scale = scaleFactorH;
    }
  }

  streamPropsChange(streamProps: IStreamProps) {
    this.streamProps = streamProps; // TODO: delete this and use this.settings.streamProps
    this.settings.streamProps = streamProps;
    isDefined(this.playerContent) && this.resizeContainer(this.playerContent.getBoundingClientRect());
  }

  toggleExportMode() {
    this.exportMode = !this.exportMode;
    if (this.exportMode && !this.isLive && !this.isPause) {
      this.pause();
    }
  }

  private attachPlayerContentResizeObserver(): void {
    this.$nextTick(() => this.observer.observe(this.playerContent));
  }

  private detachPlayerContentResizeObserver(): void {
    this.observer.disconnect();
  }
}
